<template lang="pug">
#solicitar-laudos
    header
        h1 {{ $t('laudos.solicitarLaudoTecnicoAgricola') }}

    .territorios
        header.mt-3.mb-3
            h2 {{ $t('laudos.territoriosAdicionados') }}
            BaseButton(
                type='primary',
                size='small',
                icon='el-icon-plus',
                @click='changeShowModalAddTerritorios'
            )
                | {{ $t('laudos.adicionarTerritorios') }}

        main(v-if='Object.keys(territorios).length > 0')
            CardDadosTerritorio.mb-2(
                v-for='(id, index) in Object.keys(territorios)',
                :territorio='territorios[id]',
                :index='index',
                :key='`${territorios[id].nome}_${id}`',
                :valido='cardValido[index]',
                :estados='estados',
                :municipios='municipios',
                @laudoTecnicoAgricola:atualizarMunicipios='getMunicipiosByEstado',
                @laudoTecnicoAgricola:removerTerritorio='removerTerritorio',
                @laudoTecnicoAgricola:atualizarValidacao='setValidacao',
                @laudoTecnicoAgricola:atualizarDados='setDados'
            )

        main.sem-territorios(v-else)
            a(@click='changeShowModalAddTerritorios')
                | {{ $t('laudos.adicionarTerritorios') }}

    .acoes
        BaseButton(size='small', @click='$router.go(-1)')
            | {{ $t('mensagens.comuns.voltar') }}

        BaseButton(
            v-if='Object.keys(territorios).length > 0',
            size='small',
            type='primary',
            icon='el-icon-check',
            :disabled='!todosDadosPreenchidos',
            @click='solicitarLaudos'
        )
            | {{ $t('mensagens.comuns.finalizar') }}

    el-dialog.add-territorio(
        :title='$t("laudos.selecioneUmOuMaisTerritoriosParaSolicitacaoDeLaudos")',
        :visible.sync='showModalAddTerritorios',
        close='changeShowModalAddTerritorios'
    )
        SelecionarTerritorio(
            :selecionados='territoriosSelecionados',
            @getTerritoriosSelecionados='addTerritorios'
        )
</template>

<script>
import CardDadosTerritorio from '@/modules/laudos/components/tecnicoAgricola/CardDadosTerritorio';
import SelecionarTerritorio from '@/modules/laudos/components/SelecionarTerritorio';

import TerritorioService from '@/services/TerritorioService';
import LaudosService from '@/modules/laudos/services/LaudosService';
import LocalidadeService from '@/modules/laudos/services/LocalidadeService';
import formatters from '@/modules/laudos/utils/formatters';
import BaseButton from '@/modules/laudos/components/BaseButton.vue';

import { sortBy } from 'lodash';

export default {
    name: 'SolicitarLaudoTecnicoAgricola',

    components: {
        CardDadosTerritorio,
        SelecionarTerritorio,
        BaseButton
    },

    data() {
        return {
            idsTerritoriosSelecionados: [],
            territoriosSelecionados: [],
            estados: [],
            municipios: [],
            municipiosBuffer: {},
            dados: {},
            territorios: {},
            cardValido: {},
            showModalAddTerritorios: false,
            todosDadosPreenchidos: false
        };
    },

    mounted() {
        if (this.$route.params.idsTerritorio) {
            const idsTerritoriosSelecionadosString =
                this.$route.params.idsTerritorio.split('-');
            idsTerritoriosSelecionadosString.pop();

            idsTerritoriosSelecionadosString.forEach(id =>
                this.idsTerritoriosSelecionados.push(parseInt(id))
            );
        }
        this.getTerritorios();
        this.getEstados();
    },

    methods: {
        async getTerritorios() {
            const territorios = {
                territoriosIds: this.idsTerritoriosSelecionados
            };

            const { data } = await TerritorioService.solicitacaoLaudo(
                territorios
            );

            this.territorios = {};
            data.forEach(d => {
                const id = d.id;

                this.territorios[id] = d;
                this.dados[id] = this.dados[id] || {};
                this.cardValido[id] = this.cardValido[id] || false;
            });
        },

        async getEstados() {
            const { data } = await LocalidadeService.findNivel1(1);

            sortBy(data, ['nome']).forEach(({ nome, id }) => {
                this.estados.push({
                    nome: formatters.utf8Decode(nome),
                    id
                });
            });
        },

        async getMunicipiosByEstado(idNivel1) {
            if (this.municipiosBuffer[idNivel1]) {
                this.municipios = this.municipiosBuffer[idNivel1];
            } else {
                const { data } = await LocalidadeService.findNivel2(idNivel1);

                this.municipios = [];
                data.forEach(({ nome, id }) => {
                    this.municipios.push({
                        nome: formatters.utf8Decode(nome),
                        id
                    });
                });

                this.municipiosBuffer[idNivel1] = this.municipios;
            }
        },

        removerTerritorio(id) {
            this.idsTerritoriosSelecionados.splice(
                this.idsTerritoriosSelecionados.indexOf(id),
                1
            );

            delete this.territorios[id];
            delete this.cardValido[id];
            delete this.dados[id];

            this.atualizarTerritoriosSelecionados();
            this.verificarTodosDadosPreenchidos();
        },

        changeShowModalAddTerritorios() {
            this.atualizarTerritoriosSelecionados();
            this.showModalAddTerritorios = !this.showModalAddTerritorios;
        },

        setValidacao({ valido, id }) {
            this.cardValido[id] = valido;
            this.verificarTodosDadosPreenchidos();
        },

        verificarTodosDadosPreenchidos() {
            let todosDadosPreenchidos = true;

            for (const id in this.cardValido) {
                if (this.cardValido[id] === false) {
                    todosDadosPreenchidos = false;
                    break;
                }
            }

            this.todosDadosPreenchidos = todosDadosPreenchidos;
        },

        setDados({ dados, id }) {
            this.dados[id] = dados;
        },

        solicitarLaudos() {
            if (!this.todosDadosPreenchidos) return;

            const informacaoLaudos = [];
            for (const id in this.cardValido) {
                informacaoLaudos.push(this.dados[id]);
            }

            LaudosService.cadastrarLaudos({ informacaoLaudos })
                .then(() => {
                    this.$notify.success({
                        message: this.$t(
                            'laudos.solicitacaoRealizadaComSucesso'
                        )
                    });
                    this.$router.go(-1);
                })
                .catch(error => {
                    this.$notify.error({
                        message: error.response.data.mensagem
                    });
                });
        },

        addTerritorios(territorios) {
            this.idsTerritoriosSelecionados = territorios.map(
                territorio => territorio.id
            );
            this.getTerritorios();
            this.changeShowModalAddTerritorios();
        },

        atualizarTerritoriosSelecionados() {
            this.territoriosSelecionados = [];

            for (const id in this.territorios) {
                this.territoriosSelecionados.push(this.territorios[id]);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
#solicitar-laudos {
    width: calc(100vw - 115px);
    margin: 15px auto;

    .territorios {
        header {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        .sem-territorios {
            display: flex;
            align-items: center;
            justify-content: center;

            height: 400px;

            a {
                text-decoration: underline;
            }

            a:hover {
                cursor: pointer;
            }
        }
    }

    .acoes {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
}
</style>
